import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { animated, useSpring } from '@react-spring/web';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, firstImageProperty } from '@shapeable/utils';
import { Topic } from '@shapeable/gesda-types';
import { CrossFilledIconGlyph, InfoIconGlyph } from '@shapeable/icons';
import { ContentNode, EntityImage, useLang } from '@shapeable/ui';
import { buildTopicBannerPhotoCredit } from '../../utils/topic-banner-photo-credit';
const cls = classNames('topic-banner-media');

// -------- Types -------->

const BUBBLE_SIZE_MOBILE = 85;
const BUBBLE_SIZE = 180;
/** Collapsed bubble grows once the banner track (main pane) is wide enough. */
const BUBBLE_LARGE_MIN_TRACK = 700;
const HERO_ASPECT = 16 / 9;
const COLLAPSED_TOP_MOBILE = 10;
const COLLAPSED_TOP_TABLET = 40;
const COLLAPSED_RIGHT = 12;

const COLLAPSED_HERO_IMAGE_PATHS = [
  'banner.image.thumbnails.mainBanner',
  'banner.image',
  'banner.thumbnail',
  'openGraph.image.thumbnails.full',
  'openGraph.image',
];

/** Full upload preferred — mainBanner thumbnails may be cropped for the bubble. */
const EXPANDED_HERO_IMAGE_PATHS = [
  'banner.image',
  'banner.thumbnail',
  'openGraph.image.thumbnails.full',
  'openGraph.image',
];

const heroImageAspectRatio = (width?: number, height?: number) => (
  width && height && width > 0 && height > 0 ? width / height : HERO_ASPECT
);

// -------- Props -------->

export type TopicBannerMediaProps = Classable & HasChildren & {
  entity?: Topic;
  isExpanded?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  onBubbleSizeChange?: (size: number) => void;
};

export const TopicBannerMediaDefaultProps: Omit<TopicBannerMediaProps, 'entity'> = {
  isExpanded: false,
};

// -------- Child Component Props -------->

type MediaClusterProps = {
  $isExpanded?: boolean;
};

type TopicColorProps = {
  $topicColor?: string;
};

// -------- Styles -------->

const SpacerStyles = breakpoints({
  base: css`
    width: 100%;
    flex-shrink: 0;
  `,
});

const OverlayStyles = breakpoints({
  base: css`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  `,
});

const FrameStyles = breakpoints({
  base: css`
    position: absolute;
    overflow: hidden;
    pointer-events: auto;
    cursor: ${({ $isExpanded }: MediaClusterProps) => ($isExpanded ? 'default' : 'pointer')};
    border-style: solid;
    box-sizing: border-box;
    ${theme.TRANSITION('box-shadow')};

    ${({ $topicColor }: TopicColorProps) => css`
      border-color: ${theme.COLOR($topicColor || 'primary')};
      box-shadow: 0 0 0 0 ${theme.COLOR($topicColor || 'primary')};
    `}

    ${({ $isExpanded }: MediaClusterProps) => $isExpanded && css`
      background: ${theme.COLOR('canvas')};
    `}

    /* One EntityImage fills whatever box we animate */
    .shp--entity-image,
    .shp--responsive-container {
      position: absolute !important;
      inset: 0;
      width: 100% !important;
      height: 100% !important;
      padding-top: 0 !important;
    }

    .shp--responsive-container::before,
    .shp--responsive-container::after {
      display: none !important;
    }

    .shp--responsive-container__body,
    .shp--entity-image__image,
    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: ${({ $isExpanded }: MediaClusterProps) => ($isExpanded ? 'contain' : 'cover')};
      object-position: center;
    }
  `,
});

const MediaClusterStyles = breakpoints({
  base: css`
    pointer-events: none;

    ${({ $isExpanded, $topicColor }: MediaClusterProps & TopicColorProps) => !$isExpanded && css`
      &:hover {
        ${cls.child('frame')} {
          box-shadow: 0 0 0 2px ${theme.COLOR($topicColor || 'primary')};
        }

        ${cls.child('info-button')} {
          box-shadow: 0 0 0 2px ${theme.COLOR($topicColor || 'primary')};
          ${theme.FILL($topicColor || 'primary')};
        }
      }
    `}
  `,
});

const InfoButtonStyles = breakpoints({
  base: css`
    ${theme.REMOVE_BUTTON_STYLE()};
    position: absolute;
    z-index: 3;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    background: ${theme.COLOR('dark')};
    ${({ $topicColor }: TopicColorProps) => css`
      border: 2px solid ${theme.COLOR($topicColor || 'primary')};
      box-shadow: 0 0 0 0 ${theme.COLOR($topicColor || 'primary')};
    `}
    ${theme.FILL('canvas')};
    ${theme.TRANSITION('box-shadow', 'fill', 'width', 'height')};
  `,
});

type CaptionRowStyleProps = {
  $inFlow?: boolean;
};

const CaptionRowStyles = breakpoints({
  base: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.UNIT(3)};
    pointer-events: auto;
    padding-top: ${theme.UNIT(2)};
    padding-left: ${theme.UNIT(4)};

    ${({ $inFlow }: CaptionRowStyleProps) => ($inFlow ? css`
      position: relative;
      width: 100%;
      box-sizing: border-box;
      padding-bottom: ${theme.UNIT(2)};
    ` : css`
      position: absolute;
      left: 0;
      right: 0;
    `)}
  `,

  desktopDevice: css`
    padding-left: 0;
  `,
});

const CaptionStyles = breakpoints({
  base: css`
    font-size: ${theme.FONT_SIZE(13)};
    font-weight: 300;
    color: ${theme.COLOR('mid')};
    text-decoration: underline;
    line-height: 1.4;
  `,
});

const CloseButtonStyles = breakpoints({
  base: css`
    ${theme.REMOVE_BUTTON_STYLE()};
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    ${theme.FILL('mid')};

    &:hover {
      ${theme.FILL('primary')};
      cursor: pointer;
    }
  `,
});

// -------- Components -------->

const My = {
  Spacer: styled(animated.div).attrs(cls.attr('spacer'))`${SpacerStyles}`,
  Overlay: styled.div.attrs(cls.attr('overlay'))`${OverlayStyles}`,
    MediaCluster: styled.div.attrs(cls.attr('media-cluster'))<MediaClusterProps & TopicColorProps>`${MediaClusterStyles}`,
      Frame: styled(animated.button).attrs(cls.attr('frame'))<MediaClusterProps & TopicColorProps>`${FrameStyles}`,
        Image: styled(EntityImage).attrs(cls.attr('image'))``,
      InfoButton: styled(animated.button).attrs(cls.attr('info-button'))<TopicColorProps>`${InfoButtonStyles}`,
    CaptionRow: styled(animated.div).attrs(cls.attr('caption-row'))<CaptionRowStyleProps>`${CaptionRowStyles}`,
      Caption: styled(ContentNode).attrs(cls.attr('caption'))`${CaptionStyles}`,
      CloseButton: styled.button.attrs(cls.attr('close-button'))`${CloseButtonStyles}`,
};

const usePrefersReducedMotion = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const useCollapsedTop = () => {
  const [collapsedTop, setCollapsedTop] = useState(COLLAPSED_TOP_MOBILE);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const media = window.matchMedia('(min-width: 768px)');
    const sync = () => setCollapsedTop(media.matches ? COLLAPSED_TOP_TABLET : COLLAPSED_TOP_MOBILE);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return collapsedTop;
};

export const TopicBannerMedia: Shapeable.FC<TopicBannerMediaProps> = (props) => {
  const { className, children, entity, isExpanded, onExpand, onCollapse, onBubbleSizeChange } = props;
  const t = useLang();
  const closeRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const reduceMotion = usePrefersReducedMotion();
  const collapsedTop = useCollapsedTop();
  const [trackWidth, setTrackWidth] = useState(0);

  const { banner, color } = entity || {};
  const heroImagePaths = isExpanded ? EXPANDED_HERO_IMAGE_PATHS : COLLAPSED_HERO_IMAGE_PATHS;
  const image = firstImageProperty(entity, heroImagePaths);
  const bannerImage = banner?.image;
  const expandedImageAspect = heroImageAspectRatio(bannerImage?.width, bannerImage?.height);
  const heroImageAspect = isExpanded ? expandedImageAspect : HERO_ASPECT;
  const captionLabel = buildTopicBannerPhotoCredit(banner, t('Photo'));
  const hasImage = !!image?.url;
  // Match PDF: full track width, height from native aspect — no 16:9 letterboxing when expanded
  const heroFrameAspect = isExpanded ? expandedImageAspect : HERO_ASPECT;
  const heroHeight = trackWidth ? Math.round(trackWidth / heroFrameAspect) : 0;
  // Reuse the expand-track ResizeObserver — no extra window listeners
  const bubbleSize = trackWidth >= BUBBLE_LARGE_MIN_TRACK ? BUBBLE_SIZE : BUBBLE_SIZE_MOBILE;
  const isCompactBubble = bubbleSize <= BUBBLE_SIZE_MOBILE;
  const infoButtonSize = isCompactBubble ? 20 : 28;
  const infoIconSize = isCompactBubble ? 10 : 14;
  const infoRight = isCompactBubble ? 20 : 42;

  useEffect(() => {
    onBubbleSizeChange?.(bubbleSize);
  }, [bubbleSize, onBubbleSizeChange]);

  useEffect(() => {
    const node = overlayRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      setTrackWidth(entry.contentRect.width);
    });
    observer.observe(node);
    setTrackWidth(node.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  // Size leads on expand; border clears early; radius lags for an organic morph
  const sizeSpring = useSpring({
    width: isExpanded ? Math.max(trackWidth, bubbleSize) : bubbleSize,
    height: isExpanded ? Math.max(heroHeight, bubbleSize) : bubbleSize,
    top: isExpanded ? 0 : collapsedTop,
    right: isExpanded ? 0 : COLLAPSED_RIGHT,
    infoOpacity: isExpanded ? 0 : 1,
    immediate: reduceMotion || trackWidth === 0,
    config: isExpanded
      ? { tension: 180, friction: 22 }
      : { tension: 140, friction: 26 },
  });

  const captionOpacitySpring = useSpring({
    opacity: isExpanded ? 1 : 0,
    immediate: reduceMotion || trackWidth === 0 || !isExpanded,
    config: { tension: 180, friction: 22 },
  });

  const shapeSpring = useSpring({
    borderRadius: isExpanded ? 0 : bubbleSize / 2,
    immediate: reduceMotion || trackWidth === 0,
    config: isExpanded
      ? { tension: 120, friction: 26 }
      : { tension: 220, friction: 20 },
  });

  const borderSpring = useSpring({
    borderWidth: isExpanded ? 0 : 2,
    immediate: reduceMotion || trackWidth === 0,
    config: isExpanded
      ? { tension: 320, friction: 32 }
      : { tension: 220, friction: 20 },
  });

  // Push Main down ahead of the expanding overlay so copy isn’t covered; ease back a touch slower on collapse
  const contentPush = useSpring({
    height: isExpanded && heroHeight ? heroHeight : 0,
    immediate: reduceMotion || trackWidth === 0,
    config: isExpanded
      ? { tension: 280, friction: 22 }
      : { tension: 120, friction: 26 },
  });

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCollapse?.();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isExpanded, onCollapse]);

  if (!hasImage) {
    return null;
  }

  return (
    <>
      <My.Spacer style={{ height: contentPush.height }} />

      <My.Overlay ref={overlayRef} className={cls.name(className)}>
        <My.MediaCluster $isExpanded={isExpanded} $topicColor={color?.value}>
          <My.Frame
            $topicColor={color?.value}
            type="button"
            $isExpanded={isExpanded}
            aria-label={isExpanded ? t('Topic image') : t('Expand image')}
            onClick={() => {
              if (!isExpanded) {
                onExpand?.();
              }
            }}
            style={{
              width: sizeSpring.width,
              height: sizeSpring.height,
              borderRadius: shapeSpring.borderRadius,
              borderWidth: borderSpring.borderWidth,
              top: sizeSpring.top,
              right: sizeSpring.right,
            }}
          >
            <My.Image
              entity={entity}
              paths={heroImagePaths}
              aspectRatio={{ base: heroImageAspect }}
              fill
            />
          </My.Frame>

          <My.InfoButton
            $topicColor={color?.value}
            type="button"
            aria-label={t('Image information')}
            aria-hidden={isExpanded}
            tabIndex={isExpanded ? -1 : 0}
            onClick={(event) => {
              event.stopPropagation();
              onExpand?.();
            }}
            style={{
              top: sizeSpring.top.to((value) => value),
              right: infoRight,
              width: infoButtonSize,
              height: infoButtonSize,
              opacity: sizeSpring.infoOpacity,
              pointerEvents: isExpanded ? 'none' : 'auto',
            }}
          >
            <InfoIconGlyph size={infoIconSize} />
          </My.InfoButton>
        </My.MediaCluster>
      </My.Overlay>

      {isExpanded && (
        <My.CaptionRow
          $inFlow
          style={{
            opacity: captionOpacitySpring.opacity,
          }}
        >
          {captionLabel ? <My.Caption entity={captionLabel} /> : <span />}
          <My.CloseButton
            ref={closeRef}
            type="button"
            aria-label={t('Close image')}
            tabIndex={0}
            onClick={onCollapse}
          >
            <CrossFilledIconGlyph size={28} />
          </My.CloseButton>
        </My.CaptionRow>
      )}

      {children}
    </>
  );
};

TopicBannerMedia.defaultProps = TopicBannerMediaDefaultProps;
TopicBannerMedia.cls = cls;
